import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { codingKeys } from '~/lib/query-keys';
import { type CodingSessionView } from '~/features/coding/coding-events';
import { type DesignBriefView } from '~/features/coding/design-direction';

function isAuthError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

/**
 * The user's coding sessions, most recent first (FRONT-333). Only fetched
 * while the coding window is open — `enabled` gates it so the home surface
 * doesn't poll an endpoint it never shows.
 */
export function useCodingSessions(enabled: boolean) {
  return useQuery({
    queryKey: codingKeys.list(),
    queryFn: () => api.get<{ sessions: CodingSessionView[] }>('/api/coding/sessions'),
    enabled,
    select: (data) => data.sessions,
  });
}

/**
 * FRONT-1787: the project's own context (CODE-CONTEXT.md), for the attached
 * session. Backs the transcript's "Project context" note and seeds the
 * editor dialog. Reads through the sandbox, so it is deliberately NOT folded
 * into the sessions list — that list is a hot poll and this is one file read
 * per open session.
 */
export function useCodingSessionContext(sessionId: string | null) {
  return useQuery({
    queryKey: codingKeys.context(sessionId ?? 'none'),
    queryFn: () =>
      api.get<{ context: string | null; readable: boolean }>(
        `/api/coding/sessions/${sessionId}/context`,
      ),
    enabled: !!sessionId,
    // Deliberately NOT selected down to the string. `readable: false` means
    // the sandbox read failed, which is not the same as "this project has no
    // context" — the editor has to refuse to save over content it never saw.
    // See ContextRead in src/coding/code-context-file.ts.
  });
}

/**
 * FRONT-2382: the session's "Design direction" brief (inspiration text,
 * chosen directives, staged reference images) — behind
 * `eddy.coding-design-direction`. Backs the Project-context dialog's Design
 * direction section (the New-project form's own draft never goes through
 * this hook — it holds its own local state and sends it once, on create).
 *
 * `flagOn` gates `enabled` directly, same as `sessionId` — the route 404s
 * when the flag is off, so there is no reason to ask before the caller
 * already knows it's on. Mirrors `useCodingSessionContext`'s own
 * always-prefetch-while-mounted behaviour (not gated on a dialog's own
 * `open`) so the editor has data ready the instant it opens.
 */
export function useCodingDesign(sessionId: string | null, flagOn: boolean) {
  return useQuery({
    queryKey: codingKeys.design(sessionId ?? 'none'),
    queryFn: () =>
      api.get<{ design: DesignBriefView | null }>(`/api/coding/sessions/${sessionId}/design`),
    enabled: flagOn && !!sessionId,
  });
}

/** FRONT-2935: how often an open Builder re-asks whether the app moved on. */
const DRAFT_UPDATE_POLL_MS = 60_000;

/**
 * FRONT-2935: the header's "Updates available" — whether the app has changes
 * this session's draft does not have yet. Only asked for a session saved as an
 * app; a failed check answers false server-side, so it never shows an error.
 */
export function useCodingDraftUpdate(sessionId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: codingKeys.draftUpdate(sessionId ?? 'none'),
    queryFn: () => api.get<{ available: boolean }>(`/api/coding/sessions/${sessionId}/update`),
    enabled: enabled && !!sessionId,
    refetchInterval: DRAFT_UPDATE_POLL_MS,
    select: (data) => data.available,
  });
}

/** How often the top bar re-checks for background building activity. The
 *  endpoint is a cheap per-user list; the interval only ticks while the
 *  tab is focused (TanStack's default) and the bar is mounted. */
export const ACTIVITY_POLL_MS = 15_000;

/**
 * FRONT-1681 (review round): a stable, referentially-identical fallback for
 * every caller below whose query is disabled/pending — returning a fresh
 * `[]` literal each render instead would make `barChats`'s own `useMemo` in
 * `use-chat-activity.ts` (deps include this hook's return value) recompute
 * on every render even when nothing actually changed, and hand every
 * consumer a new array reference on every render too. A `useQuery` with no
 * data returns `undefined`, never a fresh array, so this is the one place
 * that needs its own frozen constant.
 */
const EMPTY_SESSIONS = Object.freeze([]) as unknown as CodingSessionView[];

/*
 * `useCodingActivity` used to live here (FRONT-339): the poll behind the chat
 * composer's coding icon, deriving 'running'/'failed'/'done' for its coloured
 * dot. It went with the icon on 2026-09-01 — the top bar's Build button
 * replaced that doorway, and the activity signal was deliberately NOT
 * re-homed onto it, because the status cluster ten pixels away already folds
 * Building Sessions into its counts and rows (FRONT-1681) and raises the ask
 * chip for one. The pure helpers behind it went too, in
 * `features/coding/coding-events.ts` — see the note at the end of that file.
 */

/**
 * Building Sessions for the chat overview's activity board (FRONT-1681).
 * Deliberately shares `codingKeys.list()` and `ACTIVITY_POLL_MS` with
 * `useCodingSessions` rather than a second key/interval of its own — one
 * cached fetch backs the coding window's own list and this board at once, so
 * a poll response updates both together instead of drifting on separate
 * timers. `enabled` mirrors `useCodingSessions`: the in-modal
 * `ChatActivityMenu` passes this hook `includeBuilds: false` (via
 * `useChatActivity`), which disables THIS instance's own query — no new fetch
 * it wouldn't otherwise make.
 *
 * FRONT-1681 (third review round) recorded a caveat here that no longer
 * applies as written, and the reason it changed is worth keeping: a disabled
 * instance shares this query KEY, so its `data` can still be real rows some
 * OTHER enabled instance has already put in the cache, rather than the frozen
 * empty array below. The always-on poll that made that routine was
 * `useCodingActivity`, behind the chat composer's coding icon — both were
 * removed on 2026-09-01 (see the note above), so the only remaining writer to
 * this key on the chat surface is this hook's own enabled instance in the top
 * bar. Do NOT read that as "returns no data when disabled": the honest
 * invariant is still "issues no additional network request when disabled",
 * and what makes a disabled instance harmless either way is `useChatActivity`'s
 * merge (`includeBuilds ? mapCodingToBar(…) : []`), which never calls
 * `mapCodingToBar` on whatever this returns. The coding WINDOW's own
 * `useCodingSessions` still shares the key, so the hazard is dormant, not
 * gone.
 */
export function useCodingSessionsForBoard(enabled: boolean) {
  const { data } = useQuery({
    queryKey: codingKeys.list(),
    queryFn: () => api.get<{ sessions: CodingSessionView[] }>('/api/coding/sessions'),
    enabled,
    // FRONT-1681 (review round): same auth-error gate as `useActiveChats`
    // (use-chats.ts) — stopping the INTERVAL itself (not merely retries)
    // once `state.error` is a 401 is what actually ends the loop. This
    // board is mounted alongside the always-present top bar, so an expired
    // session would otherwise 401 this poll every ACTIVITY_POLL_MS forever,
    // on every page.
    refetchInterval: (q) => (isAuthError(q.state.error) ? false : ACTIVITY_POLL_MS),
    retry: (_count, err) => !isAuthError(err),
    select: (response) => response.sessions,
  });
  return data ?? EMPTY_SESSIONS;
}

/** FRONT-2962: `GET /api/coding/sessions/:id/cost`. */
export interface CodingSessionCost {
  /** Charged compute blocks plus estimates; null when no run had either. */
  computeBlocks: number | null;
  /** Some part is an estimate: show it with "~". */
  estimated: boolean;
  /** A charge is still due, so the figure is asked for again shortly. */
  pending: boolean;
}

/** How often a pending figure is re-read until its charge lands. */
export const SESSION_COST_POLL_MS = 15_000;

/**
 * FRONT-2962: what the attached session has cost — each run's platform
 * charge, or its estimate until the charge arrives. Re-read when a run ends
 * (`endedRuns` is in the key), and every few seconds while a charge is still
 * due; the server stops reporting `pending` after a bounded number of asks.
 */
export function useCodingSessionCost(sessionId: string | null, endedRuns: number) {
  return useQuery({
    queryKey: codingKeys.cost(sessionId ?? '', endedRuns),
    queryFn: () => api.get<CodingSessionCost>(`/api/coding/sessions/${encodeURIComponent(sessionId!)}/cost`),
    enabled: !!sessionId && endedRuns > 0,
    refetchInterval: (query) => (query.state.data?.pending ? SESSION_COST_POLL_MS : false),
    // A run ending is a new key; keep the last figure on screen while it loads.
    placeholderData: (previous) => previous,
  });
}
